import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Video, VideoOff, Check, Pencil, Trash2 } from "lucide-react";

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [exMeta, setExMeta] = useState({ total: 0, with_youtube: 0 });
    const [funnel, setFunnel] = useState(null);
    const [filter, setFilter] = useState("");
    const [tab, setTab] = useState("users");

    const refresh = async () => {
        const [u, a, ex, fn] = await Promise.all([
            api.get("/admin/users").then(r => r.data),
            api.get("/admin/analytics").then(r => r.data),
            api.get("/admin/exercises").then(r => r.data),
            api.get("/admin/funnel").then(r => r.data).catch(() => null),
        ]);
        setUsers(u);
        setStats(a);
        setExercises(ex.exercises || []);
        setExMeta({ total: ex.total || 0, with_youtube: ex.with_youtube || 0 });
        setFunnel(fn);
    };
    useEffect(() => { refresh(); }, []);

    const toggle = async (u) => {
        try {
            await api.post("/admin/grant-premium", { user_id: u.user_id, grant: !u.admin_override_premium });
            toast.success(`${u.admin_override_premium ? "Revoked" : "Granted"} for ${u.email}`);
            refresh();
        } catch (e) {
            toast.error("Failed");
        }
    };

    const setYoutube = async (exId, current, slot = 1) => {
        // Native prompt avoids the Brave / iOS clipboard-permission rabbit hole.
        const slotLabel = slot === 1 ? "primary" : "alternate (fallback)";
        const url = window.prompt(
            current
                ? `Paste a NEW ${slotLabel} YouTube URL (or leave blank to clear)`
                : `Paste a ${slotLabel} YouTube URL (full link or share link)`,
            current ? `https://youtu.be/${current}` : ""
        );
        if (url === null) return; // cancel
        try {
            const { data } = await api.patch(`/admin/exercises/${exId}/demo`, { youtube_url: url.trim(), slot });
            const newId = slot === 1 ? data.youtube_id : data.youtube_id_2;
            toast.success(newId ? `Slot ${slot} saved` : `Slot ${slot} cleared`);
            setExercises((list) => list.map(x => x.id === exId
                ? { ...x, [slot === 1 ? "youtube_id" : "youtube_id_2"]: newId || null }
                : x));
            if (slot === 1) {
                setExMeta((m) => ({ ...m, with_youtube: newId ? m.with_youtube + (current ? 0 : 1) : m.with_youtube - (current ? 1 : 0) }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update");
        }
    };

    const filteredExercises = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return exercises;
        return exercises.filter(x =>
            (x.name || "").toLowerCase().includes(q) ||
            (x.muscle_group || "").toLowerCase().includes(q) ||
            (x.pattern || "").toLowerCase().includes(q)
        );
    }, [exercises, filter]);

    const [syncEmail, setSyncEmail] = useState("");
    const [syncResult, setSyncResult] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const runSync = async () => {
        const email = syncEmail.trim().toLowerCase();
        if (!email) return;
        setSyncing(true);
        setSyncResult(null);
        try {
            const { data } = await api.post("/admin/sync-user", { email });
            setSyncResult(data);
            if (data.premium) {
                toast.success(`${email} unlocked — ${data.premium_reason}`);
            } else {
                toast.error(`${email} still locked: ${data.premium_reason}`);
            }
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
            setSyncResult({ error: e?.response?.data?.detail || "Sync failed" });
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="admin-page">
            <div>
                <div className="label-eyebrow text-accent-red">Owner only</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Admin</h1>
            </div>

            {stats && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <Stat label="Users" value={stats.total_users} />
                    <Stat label="Active subs" value={stats.active_subscriptions} />
                    <Stat label="On trial" value={stats.trial_users} />
                    <Stat label="Workouts" value={stats.total_workouts} />
                    <Stat label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} />
                </div>
            )}

            <div className="flex gap-2 border-b border-border">
                <button onClick={() => setTab("users")} data-testid="admin-tab-users" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${tab === "users" ? "border-b-2 border-accent-red text-accent-red" : "text-muted-foreground hover:text-foreground"}`}>Users</button>
                <button onClick={() => setTab("funnel")} data-testid="admin-tab-funnel" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${tab === "funnel" ? "border-b-2 border-accent-red text-accent-red" : "text-muted-foreground hover:text-foreground"}`}>Funnel</button>
                <button onClick={() => setTab("content")} data-testid="admin-tab-content" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${tab === "content" ? "border-b-2 border-accent-red text-accent-red" : "text-muted-foreground hover:text-foreground"}`}>Content · Exercises</button>
            </div>

            {tab === "funnel" && (
                <FunnelTab funnel={funnel} />
            )}

            {tab === "users" && (
            <div className="tactical-card p-6" data-testid="admin-sync-card">
                <div className="label-eyebrow text-accent-red">Stripe payment recovery</div>
                <p className="mt-2 text-xs text-muted-foreground">Use this when a paying user is stuck on the paywall (webhook lost / DB out of sync). Looks the user up by email, finds their Stripe customer + active subscription or most recent paid one-time checkout, and unlocks their account.</p>
                <div className="mt-4 flex flex-wrap items-stretch gap-2">
                    <input
                        type="email"
                        value={syncEmail}
                        onChange={(e) => setSyncEmail(e.target.value)}
                        placeholder="user@example.com"
                        data-testid="sync-email-input"
                        className="flex-1 min-w-[240px] rounded-sm border border-border bg-background px-3 py-2 text-sm"
                    />
                    <button
                        onClick={runSync}
                        disabled={syncing || !syncEmail.trim()}
                        data-testid="admin-sync-btn"
                        className="rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
                    >
                        {syncing ? "Syncing…" : "Sync from Stripe"}
                    </button>
                </div>
                {syncResult && (
                    <pre data-testid="sync-result" className="mt-4 max-h-72 overflow-auto rounded-sm border border-border bg-secondary/40 p-3 text-[11px] leading-relaxed">
                        {JSON.stringify(syncResult, null, 2)}
                    </pre>
                )}
            </div>
            )}

            {tab === "users" && (
            <div className="tactical-card p-6" data-testid="admin-users-card">
                <div className="label-eyebrow text-accent-red">Users</div>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[700px] text-sm">
                        <thead>
                            <tr className="label-eyebrow text-muted-foreground">
                                <th className="py-2 text-left">Email</th>
                                <th className="py-2 text-left">Name</th>
                                <th className="py-2 text-left">Role</th>
                                <th className="py-2 text-left">Status</th>
                                <th className="py-2 text-left">Premium override</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.user_id} className="border-t border-border">
                                    <td className="py-2">{u.email}</td>
                                    <td className="py-2">{u.name}</td>
                                    <td className="py-2">{u.role}</td>
                                    <td className="py-2">{u.subscription_status}</td>
                                    <td className="py-2">{u.admin_override_premium ? "On" : "Off"}</td>
                                    <td className="py-2 text-right">
                                        <button onClick={() => toggle(u)} data-testid={`toggle-${u.user_id}`} className="rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-secondary">
                                            {u.admin_override_premium ? "Revoke" : "Grant premium"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {tab === "content" && (
            <div className="tactical-card p-6" data-testid="admin-content-card">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="label-eyebrow text-accent-red">Exercise demo overrides</div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            <span className="font-bold text-foreground">{exMeta.with_youtube}/{exMeta.total}</span> exercises have a YouTube embed. Click an exercise to paste/replace its demo URL. When set, the embed replaces the default looping gif everywhere.
                        </p>
                    </div>
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                            type="text"
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            placeholder="Filter by name / muscle / pattern"
                            data-testid="admin-content-filter"
                            className="w-full rounded-sm border border-border bg-card pl-7 pr-3 py-2 text-xs focus:border-accent-red focus:outline-none sm:w-80"
                        />
                    </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[700px] text-sm">
                        <thead>
                            <tr className="label-eyebrow text-muted-foreground">
                                <th className="py-2 text-left">Exercise</th>
                                <th className="py-2 text-left">Muscle group</th>
                                <th className="py-2 text-left">Pattern</th>
                                <th className="py-2 text-left">Flags</th>
                                <th className="py-2 text-left">YT</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExercises.map(ex => (
                                <tr key={ex.id} className="border-t border-border" data-testid={`admin-ex-${ex.id}`}>
                                    <td className="py-2 font-bold">{ex.name}</td>
                                    <td className="py-2 text-xs uppercase tracking-wider text-muted-foreground">{ex.muscle_group || "—"}</td>
                                    <td className="py-2 text-xs text-muted-foreground">{ex.pattern || "—"}</td>
                                    <td className="py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                                        {ex.primary && <span className="mr-1 rounded-sm border border-accent-red/50 px-1 py-0.5 text-accent-red">★</span>}
                                        {ex.unilateral && <span className="mr-1 rounded-sm border border-border px-1 py-0.5">UNI</span>}
                                        {ex.optional && <span className="rounded-sm border border-border px-1 py-0.5">OPT</span>}
                                    </td>
                                    <td className="py-2">
                                        <div className="flex flex-col gap-1">
                                            {ex.youtube_id ? (
                                                <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid={`admin-ex-yt-on-${ex.id}`}>
                                                    <Video className="h-2.5 w-2.5" /> 1: {ex.youtube_id}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-sm border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`admin-ex-yt-off-${ex.id}`}>
                                                    <VideoOff className="h-2.5 w-2.5" /> 1: none
                                                </span>
                                            )}
                                            {ex.youtube_id_2 ? (
                                                <span className="inline-flex items-center gap-1 rounded-sm border border-sky-500/50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400" data-testid={`admin-ex-yt2-on-${ex.id}`}>
                                                    <Video className="h-2.5 w-2.5" /> 2: {ex.youtube_id_2}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-sm border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`admin-ex-yt2-off-${ex.id}`}>
                                                    <VideoOff className="h-2.5 w-2.5" /> 2: none
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2 text-right">
                                        <div className="flex flex-col items-stretch gap-1">
                                            <button onClick={() => setYoutube(ex.id, ex.youtube_id, 1)} data-testid={`admin-ex-edit-${ex.id}`} className="inline-flex items-center justify-center gap-1 rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:border-accent-red hover:text-accent-red">
                                                <Pencil className="h-3 w-3" /> {ex.youtube_id ? "Edit YT 1" : "Set YT 1"}
                                            </button>
                                            <button onClick={() => setYoutube(ex.id, ex.youtube_id_2, 2)} data-testid={`admin-ex-edit2-${ex.id}`} className="inline-flex items-center justify-center gap-1 rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:border-sky-400 hover:text-sky-400">
                                                <Pencil className="h-3 w-3" /> {ex.youtube_id_2 ? "Edit YT 2" : "Set YT 2"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredExercises.length === 0 && (
                                <tr><td colSpan={6} className="py-6 text-center text-xs text-muted-foreground">No exercises match "{filter}"</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="tactical-card p-5">
            <div className="label-eyebrow">{label}</div>
            <div className="mt-2 font-display text-3xl font-black">{value}</div>
        </div>
    );
}

// ─── Funnel tab ───────────────────────────────────────────────────────────
const WINDOW_LABELS = [
    { key: "h24", label: "Last 24 hours" },
    { key: "d7",  label: "Last 7 days" },
    { key: "d30", label: "Last 30 days" },
    { key: "all", label: "All time" },
];

function FunnelTab({ funnel }) {
    if (!funnel) {
        return (
            <div className="tactical-card p-6 text-sm text-muted-foreground" data-testid="admin-funnel-loading">
                Loading funnel…
            </div>
        );
    }
    return (
        <div className="space-y-6" data-testid="admin-funnel">
            <div className="tactical-card p-6">
                <div className="label-eyebrow text-accent-red">Acquisition funnel</div>
                <p className="mt-2 text-xs text-muted-foreground">
                    Two independent trackers for cross-validation. <span className="font-bold text-white">Page loads · server beacon</span> is the ground-truth signal — it fires from an inline script BEFORE React hydrates, so it catches fast-bouncers and users on ad-blockers that strip <code>/track/</code> POSTs. <span className="font-bold text-white">Landing views · JS tracker</span> fires after React hydrates. The gap between them = your ad-blocker / bounce attrition. Bots are filtered automatically from both.
                </p>

                <div className="mt-6 space-y-5">
                    {WINDOW_LABELS.map(({ key, label }) => {
                        const w = funnel[key] || {};
                        return (
                            <FunnelRow
                                key={key}
                                label={label}
                                testKey={key}
                                pageViews={w.page_views || 0}
                                pageViewsBots={w.page_views_bots || 0}
                                landing={w.landing_views || 0}
                                starts={w.onboarding_starts || 0}
                                completes={w.onboarding_completes || 0}
                                startRate={w.start_rate_pct || 0}
                                completeRate={w.complete_rate_pct || 0}
                                overall={w.overall_conversion_pct || 0}
                                jsGap={w.js_tracker_gap || 0}
                            />
                        );
                    })}
                </div>
            </div>

            {funnel.utm_30d && (
                <div className="tactical-card p-6" data-testid="admin-funnel-utm-card">
                    <div className="label-eyebrow text-accent-red">Ad attribution · last 30 days</div>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Bucketed from <code>?utm_source</code> / <code>?utm_medium</code> / <code>?utm_campaign</code> query params on the landing URL. Untagged Meta clicks are attributed via <code>fbclid</code>, untagged Google via <code>gclid</code>.
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                        <UtmList title="Top sources"   items={funnel.utm_30d.top_sources || []}   testKey="utm-sources"   emptyMsg="No paid traffic tagged yet. Add ?utm_source=meta to your Zeely link." />
                        <UtmList title="Top mediums"   items={funnel.utm_30d.top_mediums || []}   testKey="utm-mediums" />
                        <UtmList title="Top campaigns" items={funnel.utm_30d.top_campaigns || []} testKey="utm-campaigns" />
                    </div>
                    <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground" data-testid="utm-total">
                        Total paid page loads (any attribution marker present): <span className="font-bold text-white">{(funnel.utm_30d.total_paid_views || 0).toLocaleString()}</span>
                    </div>
                </div>
            )}

            {funnel.users && (
                <div className="tactical-card p-6" data-testid="admin-funnel-users-card">
                    <div className="label-eyebrow text-accent-red">Lifetime user snapshot</div>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Sanity check against the funnel events. Reflects every user who has ever completed the onboarding quiz —
                        including those who signed up before event tracking was added.
                    </p>
                    <div className="mt-4 grid grid-cols-3 gap-3">
                        <Stat label="Total users" value={funnel.users.total} />
                        <Stat label="Completed onboarding" value={funnel.users.onboarded} />
                        <Stat label="Completion %" value={`${funnel.users.onboarded_pct}%`} />
                    </div>
                </div>
            )}
        </div>
    );
}

function UtmList({ title, items, testKey, emptyMsg }) {
    return (
        <div data-testid={testKey}>
            <div className="label-eyebrow mb-2 text-white/60">{title}</div>
            {items.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">{emptyMsg || "No data yet"}</p>
            ) : (
                <ul className="space-y-1.5">
                    {items.map((it, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-2 border-b border-border/50 pb-1 text-xs">
                            <span className="truncate text-white/90" title={it.key}>{it.key}</span>
                            <span className="flex-none font-bold tabular-nums text-accent-red">{it.count}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FunnelRow({ label, testKey, pageViews, pageViewsBots, landing, starts, completes, startRate, completeRate, overall, jsGap }) {
    // Visual: 4 horizontal bars where the widest = page_views (or landing fallback).
    const max = Math.max(pageViews || landing || 1, 1);
    const barPct = (n) => Math.max(2, Math.round((n / max) * 100));
    return (
        <div className="border-b border-border pb-5 last:border-b-0 last:pb-0" data-testid={`funnel-row-${testKey}`}>
            <div className="flex items-baseline justify-between gap-2">
                <div className="label-eyebrow">{label}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Overall: <span className="font-bold text-accent-red" data-testid={`funnel-${testKey}-overall`}>{overall}%</span>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                <FunnelBar
                    testKey={`${testKey}-pageviews`}
                    stage="Page loads · server beacon"
                    count={pageViews}
                    pct={barPct(pageViews)}
                    color="bg-white"
                    subRate={pageViewsBots > 0 ? `+ ${pageViewsBots} bot${pageViewsBots === 1 ? "" : "s"} filtered` : "ground truth"}
                />
                <FunnelBar
                    testKey={`${testKey}-landing`}
                    stage="Landing views · JS tracker"
                    count={landing}
                    pct={barPct(landing)}
                    color="bg-white/60"
                    subRate={jsGap > 0 ? `–${jsGap} vs beacon (ad-blocked / fast bounce)` : null}
                />
                <FunnelBar testKey={`${testKey}-starts`}  stage="Onboarding started" count={starts}   pct={barPct(starts)}   color="bg-accent-red/70" subRate={`${startRate}% of loads`} />
                <FunnelBar testKey={`${testKey}-completes`} stage="Onboarding completed" count={completes} pct={barPct(completes)} color="bg-accent-red" subRate={`${completeRate}% of starters`} />
            </div>
        </div>
    );
}

function FunnelBar({ testKey, stage, count, pct, color, subRate }) {
    return (
        <div className="space-y-1" data-testid={`funnel-bar-${testKey}`}>
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
                <span className="font-bold uppercase tracking-wider text-white/85">{stage}</span>
                <span className="tabular-nums text-white/85">
                    <span className="font-bold" data-testid={`funnel-bar-${testKey}-count`}>{count.toLocaleString()}</span>
                    {subRate && <span className="ml-2 text-white/45">· {subRate}</span>}
                </span>
            </div>
            <div className="h-2 w-full overflow-hidden bg-white/5">
                <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
